import { patch } from 'api/client';
import config from 'config';
import { GenerationsApi } from 'app/modules/posts/api/GenerationsApi';
import PostApi from 'api/post';
import { MediaApi } from 'api/media';
import { imageToMediaLibraryItem } from 'utils/mediaLibrary';
import { queryKey } from 'constants/queryKey';
import Logger from 'utils/logger';
import type { QueryClient } from 'react-query';
import type { TranslationSettings } from 'app/modules/posts/modules/translation/TranslationLanguageDropdown';
import type { PostDetail } from 'types/PostDetail';

export interface TranslationLanguageOption {
  value: string;
  label: string;
}

type LocalPost = NonNullable<PostDetail['localPosts']>['data'][number];

function getTranslationParams(
  localPost: LocalPost,
  translationSettings: Record<number, TranslationSettings>,
) {
  const settings = translationSettings[localPost.page.id];
  const targetLanguage =
    settings?.translate?.postText ||
    settings?.translate?.firstComment ||
    settings?.translate?.imageText;

  if (!targetLanguage) return null;

  const translateText = !!settings?.translate?.postText;
  const translateFirstComment = !!settings?.translate?.firstComment;
  const translateImages = !!settings?.translate?.imageText;

  if (!translateText && !translateFirstComment && !translateImages) return null;

  return {
    targetLanguage,
    translateText,
    translateFirstComment,
    translateImages,
  };
}

const TranslationApi = {
  async getLanguages(): Promise<TranslationLanguageOption[]> {
    const response = await GenerationsApi.getTransformTextOptions();
    return response.options.language;
  },

  createJobsForLocalPosts({
    localPosts,
    globalPostId,
    translationSettings,
    queryClient,
    planType,
  }: {
    localPosts: LocalPost[];
    globalPostId: number;
    translationSettings: Record<number, TranslationSettings>;
    queryClient: QueryClient;
    planType?: string;
  }) {
    for (const localPost of localPosts) {
      const params = getTranslationParams(localPost, translationSettings);
      if (!params) continue;

      PostApi.createTranslationJob({
        localPostId: localPost.id,
        globalPostId,
        targetLanguage: params.targetLanguage,
        translateText: params.translateText,
        translateFirstComment: params.translateFirstComment,
        translateImages: params.translateImages,
        planType,
      })
        .then(() => {
          queryClient.invalidateQueries(queryKey.postTranslation(localPost.id));
        })
        .catch((error) => {
          Logger.error('Failed to create translation job', {
            localPostId: localPost.id,
            error,
          });
        });
    }
  },

  async replaceTranslationImage({
    postId,
    cardIndex,
    file,
  }: {
    postId: number;
    cardIndex: number;
    file: File;
  }): Promise<void> {
    const uploadResponse = await MediaApi.uploadImage({ file });
    const mediaItem = imageToMediaLibraryItem(Object.values(uploadResponse)[0]);
    if (!mediaItem?.id) {
      throw new Error('Failed to upload image');
    }

    const imageUrl =
      mediaItem.data?.original?.src || mediaItem.data?.thumbnail?.src;

    await patch(
      `/posts/${postId}/translation/image`,
      {
        cardIndex,
        translatedUrl: imageUrl,
        translatedMediumId: mediaItem.id,
      },
      { baseURL: config.API_SERVICE_ENDPOINT },
    );
  },
};

export default TranslationApi;
